import {MimeType} from "@reva/shared/mime-type";

/**
 * Bietet ein PDF (Blob) unter `filename` zum Download an; ohne Blob (Fehler beim Erzeugen) passiert nichts.
 *
 * Kein `target="_blank"`: iOS-Safari öffnet die Blob-URL sonst als Tab, ignoriert `download` und zeigt die UUID der
 * Blob-URL als Dateinamen. Der Link hängt kurz im DOM, weil Safari `download` an losen Links nicht immer auswertet.
 * @param target generated Blob or File
 * @param filename target filename
 * @param [mimeType] target mime type (default: PDF)
 */
export function openBlobForDownload(target: Blob | File | null, filename: string, mimeType: MimeType = MimeType.PDF) {
    if (target) {
        if (filename && !(target instanceof File)) {
            target = new File([target], filename, {type: mimeType});
        }
        const url = URL.createObjectURL(target);
        const a = window.document.createElement("a");
        a.href = url;
        a.download = filename;
        a.style.display = "none";
        window.document.body.appendChild(a);
        a.click();
        a.remove();
        window.setTimeout(() => {
            URL.revokeObjectURL(url);
        }, 60000);
    }
}

/**
 * Harte Navigation (neuer Seitenaufruf), z. B. damit nach der Handbuch-Seite der normale Ladeweg startet (#19302).
 * @param url Ziel-URL
 */
export function navigateHard(url: string) {
    window.location.assign(url);
}

/**
 * Check if input string is a URL (either HTTP or HTTPS).
 * @param input input to check
 * @return true, if it is a URL
 */
export function isHttpUrl(input: string): boolean {
    const trimmed = (input || "").trim();
    try {
        const url = new URL(trimmed);
        return url.protocol === "http:" || url.protocol === "https:";
    } catch (_) {
        return false;
    }
}

/**
 * Parse filename from Content-Disposition header.
 * @param contentDisposition
 * @return parse filename, or null if no valid Content-Disposition header provided
 */
export function parseFilenameFromContentDisposition(contentDisposition: string | null): string | null {
    if (!contentDisposition) return null;

    // Split header into parts (e.g., ["attachment", "filename*=UTF-8''my%20file.pdf"])
    const parts = contentDisposition.split(";").map((part) => part.trim());

    // Case 1: Handle filename* (UTF-8 encoded)
    const filenameStarPart = parts.find((part) => part.startsWith("filename*="));
    if (filenameStarPart) {
        // Regex to extract charset and encoded filename (e.g., "UTF-8''my%20file.pdf")
        const match = filenameStarPart.match(/filename\*=([^']*)''(.*)/);
        if (match) {
            const encodedFilename = match[2];
            return decodeURIComponent(encodedFilename); // Decode URL-encoded characters
        }
    }

    // Case 2: Handle filename (basic or URL-encoded)
    const filenamePart = parts.find((part) => part.startsWith("filename="));
    if (filenamePart) {
        // Remove "filename=" and trim quotes (e.g., "filename=report.pdf" → "report.pdf")
        const filename = filenamePart.replace(/^filename=/, "").replace(/["']/g, "");
        return decodeURIComponent(filename); // Decode URL-encoded characters
    }

    return null; // No filename found
}

/**
 * Build URL (for API) from components.
 * @param base API base URL
 * @param pathSegments list of API path segments to be joined to the base path (duplicate slashes in the URL are handled)
 * @param queryParams optional query parameters for the request (null and undefine values are ignored)
 */
export function buildUrl(base: string, pathSegments: string[], queryParams?: Record<string, any> | null): string {
    const url = new URL([base, ...pathSegments].join("/"), document.location.href);
    url.pathname = url.pathname.replace(/\/\/+/g, "/"); // Collapse duplicate Slashes (//) in path only
    if (typeof queryParams === "object" && queryParams) {
        for (const [key, value] of Object.entries(queryParams)) {
            if (value !== null && typeof value !== "undefined") {
                url.searchParams.append(key, value);
            }
        }
    }
    return url.toString();
}
